import Company from "./Company";
import EnrichmentInfo from "./EnrichmentInfo";
import Lead from "./Lead";
import HelpdeskTicket from "./HelpdeskTicket";
import Task from "./Task";
import Invoice from "./Invoice";
import SaleOrder from "./SaleOrder";
import PurchaseOrder from "./PurchaseOrder";

/***
 * id value for partners which have not been yet added to a Odoo database
 */
const ID_PARTNER_NOT_FROM_DATABASE: number = -1;

class Partner {
  id: number;
  name: string;
  title: string; // job title
  phone: string;
  mobile: string;
  email: string;
  companyId: number;
  company: Company;
  image: string;
  enrichmentInfo: EnrichmentInfo;
  created: boolean;
  leads: Lead[];
  tickets: HelpdeskTicket[];
  tasks: Task[];
  invoices: Invoice[];
  saleOrders: SaleOrder[];
  purchaseOrders: PurchaseOrder[];
  isCompany: boolean;
  canWriteOnPartner: boolean;

  constructor() {
    this.id = ID_PARTNER_NOT_FROM_DATABASE;
    this.name = "";
    this.title = "";
    this.phone = "";
    this.mobile = "";
    this.email = "";
    this.company = new Company();
    this.image = "";
    this.enrichmentInfo = new EnrichmentInfo();
    this.created = false;
    this.isCompany = false;
    this.canWriteOnPartner = true;
  }

  /***
   * Creates a partner which is not stored in a Odoo database
   * @param name
   * @param email
   * @param company
   */
  static createNewPartnerFromEmail = (name: string, email: string): Partner => {
    const partner = new Partner();
    partner.name = name;
    partner.email = email;
    return partner;
  };

  /**
   * Get data URL for image, detecting SVG content and using correct MIME type
   */
  static getImageDataUrl(imageBase64: string): string {
    if (!imageBase64) return "";

    const trimmed = imageBase64.trim();
    if (trimmed.startsWith("data:")) {
      return trimmed;
    }

    // Check if it's SVG by decoding first few characters
    try {
      const decoded = atob(trimmed.substring(0, Math.min(100, trimmed.length)));
      if (decoded.includes('<?xml') || decoded.includes('<svg')) {
        return `data:image/svg+xml;base64,${trimmed}`;
      }
    } catch (e) {
      // If decoding fails, assume it's a regular image
    }

    return `data:image;base64,${trimmed}`;
  }

  static fromJSON(o: Object): Partner {
    if (!o) return new Partner();
    const partner = Object.assign(new Partner(), o);
    
    // Helper function to safely extract ID from Many2one field (can be array [id, name], number, false, or null)
    const extractId = (field: any): number | undefined => {
      if (!field || field === false || field === 'False') return undefined;
      if (typeof field === 'number') return field;
      if (typeof field === 'string') {
        const trimmed = field.trim();
        if (!trimmed || trimmed === 'False') return undefined;
        const tupleMatch = trimmed.match(/^\((\d+),/);
        if (tupleMatch) {
          return Number.parseInt(tupleMatch[1], 10);
        }
        const parsedNumber = Number.parseInt(trimmed, 10);
        if (!Number.isNaN(parsedNumber)) {
          return parsedNumber;
        }
        return undefined;
      }
      if (Array.isArray(field) && field.length > 0) return field[0];
      if (typeof field === 'object' && (field as { id?: number }).id) return (field as { id?: number }).id;
      return undefined;
    };

    const normalizeBoolean = (value: any, fallback: boolean): boolean => {
      if (value === undefined || value === null) return fallback;
      if (typeof value === 'boolean') return value;
      if (typeof value === 'number') return value !== 0;
      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'false' || normalized === '0') return false;
        if (normalized === 'true' || normalized === '1') return true;
        if (!normalized) return fallback;
      }
      return Boolean(value);
    };
    
    partner.enrichmentInfo = EnrichmentInfo.fromJSON(o['enrichment_info']);
    partner.isCompany = normalizeBoolean(o['is_company'], false);
    const nestedCompany = o['company'];
    if (partner.isCompany) {
      // A company context is a res.partner record. Hydrate it from the full
      // partner response so CompanySection can use its helper methods.
      partner.company = Company.fromJSON(o, partner.enrichmentInfo);
      partner.companyId = partner.id;
    } else if (nestedCompany && typeof nestedCompany === 'object') {
      partner.company = Company.fromJSON(nestedCompany, partner.enrichmentInfo);
      partner.companyId = partner.company.id > 0 ? partner.company.id : undefined;
    } else {
      const parentId = extractId(o['parent_id']);
      const commercialPartnerId = extractId(o['commercial_partner_id']);
      partner.company = new Company();
      partner.companyId = parentId ||
        (commercialPartnerId && commercialPartnerId !== partner.id ? commercialPartnerId : undefined);
    }
    // Handle image field: Odoo returns false when no image, convert to empty string
    // Also strip Python bytes literal notation (b'...' or b"...") if present
    if (o["image_1920"] && o["image_1920"] !== false) {
      let imageData = o["image_1920"];
      // Remove Python bytes literal notation if present (b'...' or b"...")
      if (typeof imageData === 'string') {
        // Match pattern b'...' or b"..."
        const bytesLiteralMatch = imageData.match(/^b(['"])(.*)\1$/);
        if (bytesLiteralMatch) {
          imageData = bytesLiteralMatch[2]; // Extract content between quotes
        }
      }
      partner.image = imageData;
    } else {
      partner.image = "";
    }
    // Normalize phone/mobile: Odoo returns "False" string for empty fields
    partner.phone = (o['phone'] && o['phone'] !== 'False' && o['phone'] !== false) ? o['phone'] : "";
    partner.mobile = (o['mobile'] && o['mobile'] !== 'False' && o['mobile'] !== false) ? o['mobile'] : "";
    // Undefined is considered as True for retro-compatibility
    partner.canWriteOnPartner = normalizeBoolean(o['can_write_on_partner'], true);
    return partner;
  }

  /***
   * Given a list of partners having their name and/or email respectively matching a contact's name and/or email,
   * this method returns a sorted list of the best matched partners, partners are sorted according to these criteria:
   * 1) partners with an email and name which both equals the provided contact email and name
   * 2) partners with an email which is equal to the provided contact email
   * 3) partners with a name which is equal to the provided contact name
   * @param email the contact's email
   * @param name the contact's name
   * @param partners a list of partners
   */
  static sortBestMatches(email: string, name: string, partners: Partner[]): Partner[] {
    const normalize = (value?: string): string => (value || "").trim().toLowerCase();
    const normalizedEmail = normalize(email);
    const normalizedName = normalize(name);
    const emailMatches = (partner: Partner): boolean => normalize(partner.email) === normalizedEmail;
    const nameMatches = (partner: Partner): boolean => normalize(partner.name) === normalizedName;

    // Keep the original order as the final tie-breaker. Most importantly, a
    // person always wins over a company when both match the same address.
    return partners
      .map((partner, index) => ({ partner, index }))
      .sort((left, right) => {
        const emailRank = Number(emailMatches(right.partner)) - Number(emailMatches(left.partner));
        if (emailRank !== 0) return emailRank;
        const companyRank = Number(left.partner.isCompany) - Number(right.partner.isCompany);
        if (companyRank !== 0) return companyRank;
        const nameRank = Number(nameMatches(right.partner)) - Number(nameMatches(left.partner));
        if (nameRank !== 0) return nameRank;
        return left.index - right.index;
      })
      .map(entry => entry.partner);
  }

  /***
   * return a string containing the two initials of a display name, we return the initials of the first and the
   * last "words" composing the displayName, words having a length < 2 and which contain non alphabetical characters are
   * not taken into account.
   */
  getInitials(): string {
    //get all words having a length > 2 and containing only letters
    const rgx = new RegExp(/(\p{L}{1})\p{L}+/, "gu");

    const initials = [...this.name.matchAll(rgx)] || [];

    return ((initials.shift()?.[1] || "") + (initials.pop()?.[1] || "")).toUpperCase();
  }

  /***
   * Returns True if the partner exists in the Odoo database, False otherwise
   */
  isAddedToDatabase(): boolean {
    return this.id && this.id > 0;
  }
}

export default Partner;
